/**
 * Help Center sections: the ordered groups that organize help categories
 * (help_entry.module_slug) in the Help Center sidebar and home page.
 *
 * The groups mirror the app's sidebar rail (Home, Work, Sales, Support,
 * Operations, Finance) so a reader finds a module's help in the same place
 * they find the module, plus "Start here" and "Admin & system" (the same
 * label the Access Levels editor uses for workspace-control permissions).
 *
 * Every category in HELP_MODULE_FEATURE_FLAG belongs to exactly one section
 * (enforced by packages/database/src/help-content-structure.test.ts).
 */
export interface HelpCategorySection {
  key: string
  label: string
  slugs: readonly string[]
}

export const HELP_CATEGORY_SECTIONS: readonly HelpCategorySection[] = [
  { key: 'start', label: 'Start here', slugs: ['getting-started', 'benny'] },
  {
    key: 'home',
    label: 'Your day',
    slugs: ['my-work', 'focus', 'achievements', 'calendar', 'notes', 'time'],
  },
  {
    key: 'work',
    label: 'Work',
    slugs: [
      'my-organization',
      'projects',
      'clients',
      'tasks',
      'task-templates',
      'workstream',
      'deployments',
      'collections',
      'discussions',
      'documents',
      'events',
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    slugs: [
      'contacts',
      'enrichment',
      'crm',
      'marketing',
      'web-tracking',
      'scheduling',
      'communications',
    ],
  },
  {
    key: 'support',
    label: 'Support',
    slugs: ['tickets', 'live-chat', 'knowledge-base', 'forms'],
  },
  {
    key: 'operations',
    label: 'Operations',
    slugs: ['team', 'suppliers', 'products', 'workflows', 'analytics'],
  },
  {
    key: 'finance',
    label: 'Finance',
    slugs: ['financials', 'quotes', 'invoices', 'contracts', 'expenses'],
  },
  {
    key: 'admin',
    label: 'Admin & system',
    slugs: ['admin', 'settings', 'integrations', 'agents'],
  },
]

/** Fallback section for a category no section lists (e.g. admin-created). */
export const HELP_OTHER_SECTION: HelpCategorySection = {
  key: 'other',
  label: 'More topics',
  slugs: [],
}

/** The section key a category belongs to (or 'other' when unlisted). */
export function helpSectionForCategory(slug: string): string {
  return (
    HELP_CATEGORY_SECTIONS.find(section => section.slugs.includes(slug))?.key ??
    HELP_OTHER_SECTION.key
  )
}

/**
 * Group category rows into ordered sections. Rows keep the section's
 * declared order; unlisted categories collect alphabetically (by slug) in a
 * trailing "More topics" group. Empty sections are omitted.
 */
export function groupHelpCategories<T extends { slug: string }>(
  rows: readonly T[],
): Array<{ section: HelpCategorySection; rows: T[] }> {
  const bySlug = new Map(rows.map(row => [row.slug, row]))
  const listed = new Set<string>()
  const groups: Array<{ section: HelpCategorySection; rows: T[] }> = []

  for (const section of HELP_CATEGORY_SECTIONS) {
    const sectionRows: T[] = []
    for (const slug of section.slugs) {
      listed.add(slug)
      const row = bySlug.get(slug)
      if (row) sectionRows.push(row)
    }
    if (sectionRows.length > 0) groups.push({ section, rows: sectionRows })
  }

  const unlisted = rows
    .filter(row => !listed.has(row.slug))
    .sort((a, b) => a.slug.localeCompare(b.slug))
  if (unlisted.length > 0) {
    groups.push({ section: HELP_OTHER_SECTION, rows: unlisted })
  }

  return groups
}
